/**
 * Order types persisted in app_settings.enabled_order_types (JSON array).
 * Shared by Admin Settings, counter checkout, hooks, and dashboards.
 */

export const BUILTIN_ORDER_TYPE_IDS = ['walk_in', 'pickup', 'delivery'] as const
export type BuiltinOrderTypeId = (typeof BUILTIN_ORDER_TYPE_IDS)[number]

export interface OrderTypeConfig {
  id: string
  label: string
  enabled: boolean
  delivery_fee?: number
  /** Hex RGB e.g. #0d9488 — used on counter tabs and charts */
  color?: string
  /** Present when row is a system type (immutable id); omit or false for custom types */
  builtin?: boolean
  /** Lower sorts first among custom types */
  sort_order?: number
}

export const DEFAULT_BUILTIN_ORDER_TYPES: OrderTypeConfig[] = [
  {
    id: 'walk_in',
    label: 'Walk-in',
    enabled: true,
    delivery_fee: 0,
    color: '#22c55e',
    builtin: true,
  },
  {
    id: 'pickup',
    label: 'Pickup',
    enabled: true,
    delivery_fee: 0,
    color: '#f97316',
    builtin: true,
  },
  {
    id: 'delivery',
    label: 'Delivery',
    enabled: true,
    delivery_fee: 0,
    color: '#3b82f6',
    builtin: true,
  },
]

const HEX_RE = /^#([0-9a-fA-F]{6})$/

export function isBuiltinOrderTypeId(id: string): boolean {
  const n = normalizeOrderTypeId(id)
  return (BUILTIN_ORDER_TYPE_IDS as readonly string[]).includes(n)
}

/** Lowercase built-in aliases; preserve custom ids as trimmed */
export function normalizeOrderTypeId(id: string): string {
  const t = String(id ?? '').trim()
  const lower = t.toLowerCase()
  if ((BUILTIN_ORDER_TYPE_IDS as readonly string[]).includes(lower)) return lower
  return t
}

export function sanitizeOrderTypeHexColor(raw: string | undefined, fallback: string): string {
  const s = String(raw ?? '').trim()
  if (HEX_RE.test(s)) return s.toLowerCase()
  return fallback
}

export function generateCustomOrderTypeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ot_${crypto.randomUUID().replace(/-/g, '')}`
  }
  return `ot_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Merge persisted JSON with defaults: three built-ins first (always), then custom rows sorted.
 */
export function normalizeEnabledOrderTypes(persisted: unknown): OrderTypeConfig[] {
  const rows = Array.isArray(persisted) ? (persisted as OrderTypeConfig[]) : []
  const byNormId = new Map<string, OrderTypeConfig>()
  for (const r of rows) {
    if (!r || typeof r.id !== 'string' || !r.id.trim()) continue
    const nid = normalizeOrderTypeId(r.id)
    byNormId.set(nid, { ...r, id: nid })
  }

  const builtins: OrderTypeConfig[] = DEFAULT_BUILTIN_ORDER_TYPES.map((def) => {
    const existing = byNormId.get(def.id)
    if (!existing) return { ...def }
    const dFee =
      typeof existing.delivery_fee === 'number' && !Number.isNaN(existing.delivery_fee)
        ? existing.delivery_fee
        : 0
    return {
      ...def,
      enabled: existing.enabled,
      label: (existing.label && String(existing.label).trim()) || def.label,
      delivery_fee: def.id === 'delivery' ? Math.max(0, dFee) : 0,
      color: sanitizeOrderTypeHexColor(existing.color, def.color ?? '#64748b'),
      builtin: true,
    }
  })

  const builtinSet = new Set<string>(BUILTIN_ORDER_TYPE_IDS as unknown as string[])
  const customs: OrderTypeConfig[] = []
  for (const [nid, row] of byNormId) {
    if (builtinSet.has(nid)) continue
    // Built explicitly (not spread) so stale keys in persisted JSON (e.g. the
    // pre-F-RETAIL-04 service-charge flag) do not ride along.
    customs.push({
      id: row.id.trim(),
      label: (row.label && String(row.label).trim()) || row.id,
      enabled: Boolean(row.enabled),
      delivery_fee: 0,
      color: sanitizeOrderTypeHexColor(row.color, '#64748b'),
      builtin: false,
      sort_order: typeof row.sort_order === 'number' && !Number.isNaN(row.sort_order) ? row.sort_order : 999,
    })
  }
  customs.sort((a, b) => {
    const sa = a.sort_order ?? 999
    const sb = b.sort_order ?? 999
    if (sa !== sb) return sa - sb
    return a.label.localeCompare(b.label)
  })

  return [...builtins, ...customs]
}

/** Enabled ids for checkout tabs — includes custom types */
export function enabledIdsFromConfig(rows: OrderTypeConfig[]): ReadonlySet<string> {
  const s = new Set<string>()
  if (!rows.length) {
    for (const id of BUILTIN_ORDER_TYPE_IDS) s.add(id)
    return s
  }
  for (const r of rows) {
    if (r.enabled) s.add(normalizeOrderTypeId(r.id))
  }
  return s
}

/** Labels keyed by normalized id (all rows, not only enabled) */
export function orderTypeLabelMap(rows: OrderTypeConfig[]): Map<string, string> {
  const m = new Map<string, string>()
  for (const r of rows) {
    m.set(normalizeOrderTypeId(r.id), r.label || r.id)
  }
  return m
}

/** Resolve accent hex for an order type id */
export function colorForOrderTypeId(rows: OrderTypeConfig[], id: string): string | undefined {
  const nid = normalizeOrderTypeId(id)
  const row = rows.find((r) => normalizeOrderTypeId(r.id) === nid)
  if (!row?.color) return undefined
  return sanitizeOrderTypeHexColor(row.color, '#64748b')
}

export function displayLabelForOrderType(id: string, rows: OrderTypeConfig[]): string {
  const nid = normalizeOrderTypeId(id)
  const row = rows.find((r) => normalizeOrderTypeId(r.id) === nid)
  const lb = row?.label?.trim()
  if (lb) return lb
  return nid.replace(/_/g, ' ')
}

function linearizeSrgb(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** Pick readable text color on top of a solid hex background */
export function contrastingForeground(hexInput: string): '#ffffff' | '#0f172a' {
  const hex = sanitizeOrderTypeHexColor(hexInput, '#64748b').slice(1)
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255
  const L = 0.2126 * linearizeSrgb(r) + 0.7152 * linearizeSrgb(g) + 0.0722 * linearizeSrgb(b)
  return L > 0.55 ? '#0f172a' : '#ffffff'
}
